import { useEffect, useMemo, useRef, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api, { getApiError } from '../../lib/axios';
import {
  Handshake, Plus, Search, Upload, Filter, Mail, ExternalLink, X,
  Loader2, Check, Inbox, ArrowRight, Trash2, FileUp,
} from 'lucide-react';
import type { Partner, PartnerStatus, PartnerLead, PartnerStats } from '../../types';

const STATUSES: PartnerStatus[] = [
  'prospect', 'contacted', 'replied', 'negotiating', 'integrated', 'live', 'rejected', 'archived',
];

const STATUS_STYLES: Record<PartnerStatus, string> = {
  prospect:    'bg-slate-100 text-slate-700 border-slate-200',
  contacted:   'bg-blue-50 text-blue-700 border-blue-200',
  replied:     'bg-amber-50 text-amber-700 border-amber-200',
  negotiating: 'bg-purple-50 text-purple-700 border-purple-200',
  integrated:  'bg-teal-50 text-teal-700 border-teal-200',
  live:        'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected:    'bg-rose-50 text-rose-700 border-rose-200',
  archived:    'bg-slate-50 text-slate-500 border-slate-200',
};

type Tab = 'pipeline' | 'leads';

export default function AdminPartners() {
  const [tab, setTab] = useState<Tab>('pipeline');

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <header className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
              <Handshake className="w-6 h-6 text-brand-600" /> Marketplace partnerships
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Outreach pipeline + inbound leads from the /partners landing page.
            </p>
          </div>
          <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
            <TabButton active={tab === 'pipeline'} onClick={() => setTab('pipeline')} icon={Handshake} label="Pipeline" />
            <TabButton active={tab === 'leads'} onClick={() => setTab('leads')} icon={Inbox} label="Inbox" />
          </div>
        </header>

        {tab === 'pipeline' ? <Pipeline /> : <LeadsInbox onPromote={() => setTab('pipeline')} />}
      </div>
    </DashboardLayout>
  );
}

function TabButton({ active, onClick, icon: Icon, label }: {
  active: boolean; onClick: () => void; icon: typeof Handshake; label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
        active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

// ───────────────────────── Pipeline tab ─────────────────────────
function Pipeline() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [stats, setStats] = useState<PartnerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PartnerStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<Partner | null>(null);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const csvRef = useRef<HTMLInputElement>(null);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search.trim()) params.set('q', search.trim());
      const res = await api.get(`/api/admin/partners?${params.toString()}`);
      setPartners(res.data.partners);
      const statsRes = await api.get('/api/admin/partners/stats');
      setStats(statsRes.data);
      setError(null);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartners(); }, [statusFilter]);
  useEffect(() => {
    const t = setTimeout(fetchPartners, 300);
    return () => clearTimeout(t);
  }, [search]);

  const onImportCsv = async (file: File) => {
    setImporting(true);
    setImportMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/api/admin/partners/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { created, updated, skipped, errors } = res.data;
      setImportMessage(
        `Imported: ${created} new, ${updated} updated, ${skipped} skipped${errors?.length ? ` (${errors.length} errors)` : ''}`,
      );
      await fetchPartners();
    } catch (err) {
      setImportMessage(`Import failed: ${getApiError(err)}`);
    } finally {
      setImporting(false);
      if (csvRef.current) csvRef.current.value = '';
    }
  };

  return (
    <>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-6">
          <StatChip label="Total" value={stats.total} active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
          {STATUSES.map(s => (
            <StatChip
              key={s}
              label={s}
              value={stats.by_status[s] ?? 0}
              active={statusFilter === s}
              onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, website, email..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
          />
        </div>
        <button
          onClick={() => csvRef.current?.click()}
          disabled={importing}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Import CSV
        </button>
        <input
          ref={csvRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) onImportCsv(f); }}
        />
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" /> Add partner
        </button>
      </div>

      {importMessage && (
        <div className="mb-4 p-3 text-sm rounded-lg bg-brand-50 border border-brand-200 text-brand-800">
          {importMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 text-sm rounded-lg bg-rose-50 border border-rose-200 text-rose-700">
          {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th className="hidden sm:table-cell">Contact</Th>
              <Th className="hidden md:table-cell text-right">Domains</Th>
              <Th className="hidden lg:table-cell">Countries</Th>
              <Th className="text-right">Priority</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : partners.length === 0 ? (
              <tr><td colSpan={7} className="py-10 text-center text-slate-400">No partners yet. Import the starter CSV or add one.</td></tr>
            ) : partners.map(p => (
              <tr key={p.id} onClick={() => setSelected(p)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{p.name}</div>
                  {p.website_url && (
                    <a
                      href={p.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="text-xs text-slate-500 hover:text-brand-600 inline-flex items-center gap-1"
                    >
                      {p.website_url.replace(/^https?:\/\//, '')} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_STYLES[p.status]}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  {p.contact_email ? (
                    <a
                      href={`mailto:${p.contact_email}`}
                      onClick={e => e.stopPropagation()}
                      className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1"
                    >
                      <Mail className="w-3 h-3" /> {p.contact_email}
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-right text-xs text-slate-600">
                  {p.domain_count_estimate != null ? p.domain_count_estimate.toLocaleString() : '—'}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">{p.countries || '—'}</td>
                <td className="px-4 py-3 text-right text-xs font-semibold text-slate-700">{p.priority}</td>
                <td className="px-4 py-3 text-right text-slate-300">
                  <ArrowRight className="w-4 h-4 inline" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreatePartnerModal onClose={() => setShowCreate(false)} onCreated={p => { setPartners(prev => [p, ...prev]); setShowCreate(false); fetchPartners(); }} />}
      {selected && <PartnerDetailDrawer partner={selected} onClose={() => setSelected(null)} onChange={updated => { setPartners(prev => prev.map(x => x.id === updated.id ? updated : x)); setSelected(updated); }} onDelete={id => { setPartners(prev => prev.filter(x => x.id !== id)); setSelected(null); fetchPartners(); }} />}
    </>
  );
}

function StatChip({ label, value, active, onClick }: { label: string; value: number; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 rounded-xl border text-left transition-all ${
        active ? 'bg-brand-50 border-brand-300' : 'bg-white border-slate-200 hover:border-slate-300'
      }`}
    >
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-extrabold text-slate-900">{value}</div>
    </button>
  );
}

function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 ${className}`}>
      {children}
    </th>
  );
}

// ───────────────────────── Create partner modal ─────────────────────────
function CreatePartnerModal({ onClose, onCreated }: { onClose: () => void; onCreated: (p: Partner) => void }) {
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [priority, setPriority] = useState('50');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError('Name is required');
    setSubmitting(true);
    try {
      const res = await api.post('/api/admin/partners', {
        name: name.trim(),
        website_url: website.trim() || null,
        contact_email: email.trim() || null,
        priority: Math.max(0, Math.min(100, parseInt(priority, 10) || 0)),
      });
      onCreated(res.data);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Add partner">
      <form onSubmit={submit} className="space-y-4">
        <LabeledInput label="Name" required value={name} onChange={setName} placeholder="e.g. Collaborator" />
        <LabeledInput label="Website URL" value={website} onChange={setWebsite} placeholder="https://..." />
        <LabeledInput label="Contact email" value={email} onChange={setEmail} type="email" placeholder="partners@..." />
        <LabeledInput label="Priority (0-100)" value={priority} onChange={setPriority} type="number" />
        {error && <div className="text-sm text-rose-600">{error}</div>}
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button type="submit" disabled={submitting} className="px-4 py-2 text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50">
            {submitting ? 'Adding...' : 'Add partner'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ───────────────────────── Partner detail drawer ─────────────────────────
function PartnerDetailDrawer({
  partner, onClose, onChange, onDelete,
}: {
  partner: Partner;
  onClose: () => void;
  onChange: (p: Partner) => void;
  onDelete: (id: string) => void;
}) {
  const [draft, setDraft] = useState<Partner>(partner);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const invRef = useRef<HTMLInputElement>(null);
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(partner), [draft, partner]);

  useEffect(() => { setDraft(partner); }, [partner.id]);

  const save = async (payload: Partial<Partner> & { mark_contacted?: boolean } = {}) => {
    setSaving(true);
    setMessage(null);
    try {
      const body: Record<string, unknown> = {
        name: draft.name,
        website_url: draft.website_url,
        contact_name: draft.contact_name,
        contact_email: draft.contact_email,
        affiliate_program_url: draft.affiliate_program_url,
        api_docs_url: draft.api_docs_url,
        linkedin_url: draft.linkedin_url,
        domain_count_estimate: draft.domain_count_estimate,
        countries: draft.countries,
        notes: draft.notes,
        status: draft.status,
        priority: draft.priority,
        ...payload,
      };
      const res = await api.patch(`/api/admin/partners/${partner.id}`, body);
      onChange(res.data);
      setMessage('Saved.');
      setTimeout(() => setMessage(null), 2000);
    } catch (err) {
      setMessage(getApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadInventory = async (file: File) => {
    setUploading(true);
    setMessage(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/api/admin/partners/${partner.id}/inventory`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { domains_created, domains_updated, listings_created, listings_skipped, errors } = res.data;
      setMessage(
        `Inventory: +${domains_created} new domains, ${domains_updated} updated, +${listings_created} listings${listings_skipped ? `, ${listings_skipped} skipped` : ''}${errors?.length ? ` (${errors.length} errors)` : ''}`,
      );
      // Refresh partner (status may have moved to 'live')
      const p = (await api.get(`/api/admin/partners/${partner.id}`)).data;
      onChange(p);
    } catch (err) {
      setMessage(`Upload failed: ${getApiError(err)}`);
    } finally {
      setUploading(false);
      if (invRef.current) invRef.current.value = '';
    }
  };

  const remove = async () => {
    if (!confirm(`Delete ${partner.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/partners/${partner.id}`);
      onDelete(partner.id);
    } catch (err) {
      setMessage(getApiError(err));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-slate-900/50" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl animate-slide-in-right">
        <header className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900 truncate">{partner.name}</h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border mt-1 ${STATUS_STYLES[partner.status]}`}>
              {partner.status}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
        </header>

        <div className="p-5 space-y-5">
          {message && (
            <div className="p-3 text-sm rounded-lg bg-brand-50 border border-brand-200 text-brand-800">{message}</div>
          )}

          <Section title="Research">
            <Row>
              <DraftField label="Name" value={draft.name} onChange={v => setDraft({ ...draft, name: v })} />
              <DraftField label="Website URL" value={draft.website_url || ''} onChange={v => setDraft({ ...draft, website_url: v || null })} />
            </Row>
            <Row>
              <DraftField label="Contact name" value={draft.contact_name || ''} onChange={v => setDraft({ ...draft, contact_name: v || null })} />
              <DraftField label="Contact email" type="email" value={draft.contact_email || ''} onChange={v => setDraft({ ...draft, contact_email: v || null })} />
            </Row>
            <Row>
              <DraftField label="Affiliate program URL" value={draft.affiliate_program_url || ''} onChange={v => setDraft({ ...draft, affiliate_program_url: v || null })} />
              <DraftField label="LinkedIn URL" value={draft.linkedin_url || ''} onChange={v => setDraft({ ...draft, linkedin_url: v || null })} />
            </Row>
            <DraftField label="API docs URL" value={draft.api_docs_url || ''} onChange={v => setDraft({ ...draft, api_docs_url: v || null })} />
            <Row>
              <DraftField label="Domain count (est.)" type="number" value={draft.domain_count_estimate?.toString() || ''} onChange={v => setDraft({ ...draft, domain_count_estimate: v ? Math.max(0, parseInt(v, 10)) : null })} />
              <DraftField label="Countries" value={draft.countries || ''} onChange={v => setDraft({ ...draft, countries: v || null })} />
            </Row>
          </Section>

          <Section title="Pipeline">
            <Row>
              <label className="block flex-1">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</span>
                <select
                  value={draft.status}
                  onChange={e => setDraft({ ...draft, status: e.target.value as PartnerStatus })}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                >
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <DraftField label="Priority (0-100)" type="number" value={draft.priority.toString()} onChange={v => setDraft({ ...draft, priority: Math.max(0, Math.min(100, parseInt(v, 10) || 0)) })} />
            </Row>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Notes</span>
              <textarea
                value={draft.notes || ''}
                onChange={e => setDraft({ ...draft, notes: e.target.value || null })}
                rows={4}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 resize-none"
              />
            </label>

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                onClick={() => save({ mark_contacted: true })}
                disabled={saving}
                className="px-3 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <Mail className="w-4 h-4" /> Mark contacted
              </button>
              <button
                onClick={() => save()}
                disabled={saving || !dirty}
                className="px-3 py-2 text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {dirty ? 'Save changes' : 'Saved'}
              </button>
            </div>
          </Section>

          <Section title="Inventory">
            <p className="text-sm text-slate-500 mb-3">
              Upload a CSV of this partner's domain + price inventory.
              Required columns: <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">domain</code>, <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">price</code>.
              Optional: <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">dr, da, cf, tf, ref_domains, organic_traffic, topics, country, category, listing_type, currency</code>.
            </p>
            <button
              onClick={() => invRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
              Upload inventory CSV
            </button>
            <input
              ref={invRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) uploadInventory(f); }}
            />
          </Section>

          <Section title="Danger zone">
            <button
              onClick={remove}
              className="px-3 py-2 text-sm font-semibold text-rose-700 border border-rose-200 rounded-lg hover:bg-rose-50 inline-flex items-center gap-1.5"
            >
              <Trash2 className="w-4 h-4" /> Delete partner
            </button>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col sm:flex-row gap-3">{children}</div>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
        <Filter className="w-3 h-3" /> {title}
      </h4>
      {children}
    </section>
  );
}

function DraftField({
  label, value, onChange, type = 'text',
}: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <label className="block flex-1">
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</span>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
      />
    </label>
  );
}

function LabeledInput({
  label, value, onChange, required, type = 'text', placeholder,
}: { label: string; value: string; onChange: (v: string) => void; required?: boolean; type?: string; placeholder?: string }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
      />
    </label>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
        </header>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ───────────────────────── Leads tab ─────────────────────────
function LeadsInbox({ onPromote }: { onPromote: () => void }) {
  const [leads, setLeads] = useState<PartnerLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | PartnerLead['handled']>('all');

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? '' : `?handled=${filter}`;
      const res = await api.get(`/api/admin/partners/leads/list${params}`);
      setLeads(res.data);
      setError(null);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, [filter]);

  const mark = async (id: string, handled: PartnerLead['handled']) => {
    await api.patch(`/api/admin/partners/leads/${id}`, { handled });
    fetchLeads();
  };

  const convert = async (id: string) => {
    try {
      await api.post(`/api/admin/partners/leads/${id}/convert`);
      await fetchLeads();
      onPromote();
    } catch (err) {
      setError(getApiError(err));
    }
  };

  return (
    <>
      <div className="flex gap-2 mb-4 flex-wrap">
        {(['all', 'new', 'reviewing', 'converted', 'spam'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              filter === f ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-600 border-slate-200 hover:border-brand-300'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {error && <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700 mb-4">{error}</div>}

      <div className="space-y-3">
        {loading ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : leads.length === 0 ? (
          <div className="p-10 text-center text-slate-400">No leads yet. Share /partners with marketplaces to start collecting signups.</div>
        ) : leads.map(lead => (
          <div key={lead.id} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-colors">
            <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
              <div>
                <h3 className="text-base font-bold text-slate-900">{lead.company_name}</h3>
                <div className="text-sm text-slate-500">
                  {lead.contact_name ? `${lead.contact_name} · ` : ''}
                  <a href={`mailto:${lead.email}`} className="text-brand-600 hover:underline">{lead.email}</a>
                </div>
                {lead.website && (
                  <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-brand-600 inline-flex items-center gap-1 mt-1">
                    {lead.website.replace(/^https?:\/\//, '')} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                lead.handled === 'new' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                lead.handled === 'reviewing' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                lead.handled === 'converted' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                'bg-slate-100 text-slate-600 border-slate-200'
              }`}>
                {lead.handled}
              </span>
            </div>
            {lead.domain_count_estimate != null && (
              <p className="text-xs text-slate-500 mb-2">Est. inventory: {lead.domain_count_estimate.toLocaleString()} domains</p>
            )}
            {lead.message && (
              <p className="text-sm text-slate-700 mb-3 p-3 bg-slate-50 rounded-lg">{lead.message}</p>
            )}
            <div className="flex flex-wrap gap-2">
              {lead.handled !== 'converted' && (
                <button onClick={() => convert(lead.id)} className="px-3 py-1.5 text-xs font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg inline-flex items-center gap-1">
                  <ArrowRight className="w-3 h-3" /> Convert to partner
                </button>
              )}
              {lead.handled === 'new' && (
                <button onClick={() => mark(lead.id, 'reviewing')} className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg">
                  Mark reviewing
                </button>
              )}
              {lead.handled !== 'spam' && (
                <button onClick={() => mark(lead.id, 'spam')} className="px-3 py-1.5 text-xs font-semibold text-rose-700 bg-white border border-rose-200 hover:bg-rose-50 rounded-lg">
                  Spam
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
